<script setup lang="ts">
import { autoUpdate, flip, offset, useFloating } from '@floating-ui/vue';
import { onClickOutside } from '@vueuse/core';
import { useAgentStats } from '@/api/queries/agent';
import PortalTopNavPreferences from '@/components/portal/TopNavPreferences.vue';
import useLogout from '@/composables/auth/useLogout';
import { convertCurrency } from '@/helpers/currency.helper';
import { USER_ROLES } from '~/constants/auth';
import type { LocaleCode } from '~/constants/i18n';
import { getLocaleMeta, stripLocalePrefix } from '~/utils/i18n/locale';
import { isLearnPath } from '~/constants/portalNav';

type TabKey =
  | 'dashboard'
  | 'learn'
  | 'deals'
  | 'explore'
  | 'projects'
  | 'developers'
  | 'resale'
  | 'news'
  | 'tiers'
  | 'profile'
  | 'services'
  | 'myServices'
  | 'marketing';

type SubItem = { key: TabKey; label: string; description: string; to: string };

type Tab = {
  key: TabKey;
  label: string;
  to?: string;
  isNew?: boolean;
  submenu?: SubItem[];
};

const { t } = useI18n();
const route = useRoute();
const authStore = useAuthStore();
const uiStore = useUIStore();

const { data: agentStats } = useAgentStats();
const { mutateAsync: logout } = useLogout();

// Marketing is shown to verified agents, free-plan users and entitled users (same gate as
// the floating Ask launcher); the per-feature limit + upsell modal handle conversion.
const { isEntitled, canSeeFreeFeatures } = useCanSeeFreeFeatures();

// Staff (Admin / SDR / Account Manager) get the secondary market as an internal deal surface,
// even though they have no paid entitlement.
const isStaff = computed(() => {
  const r = authStore.user?.role;
  return r === USER_ROLES.ADMIN || r === USER_ROLES.SDR || r === USER_ROLES.ACCOUNT_MANAGER;
});
const canSee24Sold = computed(() => isEntitled.value || isStaff.value);

// UAE agents register their bookings under Insiders' licence, so its details sit in the avatar menu.
const { isUae } = useUaeGate();
const showInsidersLicence = computed(() => isUae.value || isStaff.value);

// Services (catalogue + "My services" orders) is for international agents; UAE agents don't
// get it in their menu. The pages still resolve by URL.
const showServices = computed(() => !isUae.value);

// Masterclass leads get full agent-equivalent platform access — they browse
// every surface to "see the value" while only the Masterclass course itself
// stays gated behind the $197 unlock (handled inside /learn). So the nav is no
// longer trimmed for leads; the only conditional left is the verified-gate on
// the Marketing tab below.
const exploreSubmenuAll = computed<SubItem[]>(() => [
  {
    key: 'projects',
    label: t('nav.items.projects'),
    description: t('nav.topNav.explore.projectsDescription'),
    to: '/explore',
  },
  {
    key: 'developers',
    label: t('nav.items.developers'),
    description: t('nav.topNav.explore.developersDescription'),
    to: '/developers',
  },
  {
    key: 'resale',
    label: t('nav.items.secondaryMarket'),
    description: t('nav.topNav.explore.resaleDescription'),
    to: '/resale',
  },
]);

// Profile and Tiers moved into the avatar dropdown — they're personal/account
// surfaces, not primary navigation, and the top bar was getting cramped.
const tabs = computed<Tab[]>(() => {
  const submenu = canSee24Sold.value
    ? exploreSubmenuAll.value
    : exploreSubmenuAll.value.filter((s) => s.key !== 'resale');
  // Ask lives in the floating launcher on every page instead of a tab. Learn holds the
  // Masterclass, Market & Sales and the Developer Vault as categories of one tab.
  const all: Tab[] = [
    { key: 'dashboard', label: t('nav.items.dashboard'), to: '/dashboard' },
    { key: 'learn', label: t('nav.items.learn'), to: '/learn' },
    { key: 'deals', label: t('nav.items.dealsShort'), to: '/deals' },
    // Secondary market is members-only (trial/paid/granted) — hidden from preview.
    {
      key: 'explore',
      label: t('nav.items.explore'),
      submenu,
    },
    { key: 'marketing', label: t('nav.items.marketing'), to: '/marketing' },
    { key: 'news', label: t('nav.items.news'), to: '/news' },
    ...(showServices.value ? [{ key: 'services' as TabKey, label: t('nav.items.services'), to: '/services' }] : []),
  ];
  // Marketing is verified-phone gated — but free-tier leads get a free quota,
  // so show it to them too (canSeeFreeFeatures covers verified OR free-tier).
  const verifiedOnly = new Set(['marketing']);
  return canSeeFreeFeatures.value ? all : all.filter((t) => !verifiedOnly.has(t.key));
});

const paths = computed(() => ({
  dashboard: '/dashboard',
  deals: '/deals',
  projects: '/explore',
  developers: '/developers',
  marketing: '/marketing',
  news: '/news',
  tiers: '/rankings',
  profile: '/profile',
  services: '/services',
}));

const activeKey = computed<TabKey | null>(() => {
  const p = stripLocalePrefix(route.path);

  if (isLearnPath(p)) return 'learn';
  if (p.startsWith(paths.value.services)) return 'services';
  if (p.startsWith(paths.value.deals)) return 'deals';
  if (p.startsWith(paths.value.developers) || p.startsWith(paths.value.projects) || p.startsWith('/resale'))
    return 'explore';
  if (p.startsWith(paths.value.marketing)) return 'marketing';
  if (p.startsWith(paths.value.news)) return 'news';
  if (p.startsWith(paths.value.tiers)) return 'tiers';
  if (p.startsWith(paths.value.profile)) return 'profile';
  if (p.startsWith(paths.value.dashboard)) return 'dashboard';

  return null;
});

const clientMode = computed(() => uiStore.clientMode);

const prefsStore = usePreferencesStore();

const earningsAed = computed<number | null>(() => {
  // Prefer lifetime totalCommission for the "Your earnings" pill (matches design v12).
  const total = agentStats.value?.totalCommission;
  if (total !== undefined && total !== null) return Number(total);
  const balance = agentStats.value?.commissionBalance;
  if (balance === undefined || balance === null) return null;
  return Number(balance);
});

const showEarnings = computed(() => !clientMode.value && earningsAed.value !== null);

// Compact earnings display: "0 AED", "12.4K AED", "1.2M AED". The pill is
// width-constrained in the nav bar so the full "1,000,000 AED" form would
// either wrap onto two lines or push neighbours off-screen.
const compactFormatter = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});
const earningsDisplay = computed(() => {
  const aed = earningsAed.value ?? 0;
  const converted = convertCurrency(aed, 'AED', prefsStore.currency);
  return `${compactFormatter.format(converted)} ${prefsStore.currency}`;
});

const userInitials = computed(() => {
  const first = authStore.user?.firstName?.[0] ?? '';
  const last = authStore.user?.lastName?.[0] ?? '';
  const initials = `${first}${last}`.trim().toUpperCase();
  return initials || authStore.user?.email?.[0]?.toUpperCase() || '?';
});

const userFullName = computed(() => {
  const first = authStore.user?.firstName ?? '';
  const last = authStore.user?.lastName ?? '';
  return `${first} ${last}`.trim() || authStore.user?.email || '';
});

const isMenuVisible = ref(false);
const activator = ref<HTMLDivElement | null>(null);
const menu = ref<HTMLDivElement | null>(null);

const { floatingStyles } = useFloating(activator, menu, {
  placement: 'bottom-end',
  strategy: 'fixed',
  whileElementsMounted: autoUpdate,
  middleware: [flip({ fallbackPlacements: ['top-end'] }), offset(8)],
  open: isMenuVisible,
});

onClickOutside(
  menu,
  () => {
    isMenuVisible.value = false;
  },
  { ignore: [activator] },
);

const handleLogout = async () => {
  isMenuVisible.value = false;
  await logout();
};

const handleMenuLinkClick = () => {
  isMenuVisible.value = false;
};

// ── Explore mega-dropdown (Projects + Developers + Resale) ───────────
const exploreSubmenu = computed<SubItem[]>(() => tabs.value.find((t) => t.key === 'explore')?.submenu ?? []);
const isExploreOpen = ref(false);
const exploreActivator = ref<HTMLDivElement | null>(null);
const exploreMenu = ref<HTMLDivElement | null>(null);

const { floatingStyles: exploreFloatingStyles } = useFloating(exploreActivator, exploreMenu, {
  placement: 'bottom-start',
  strategy: 'fixed',
  whileElementsMounted: autoUpdate,
  middleware: [flip({ fallbackPlacements: ['top-start'] }), offset(0)],
  open: isExploreOpen,
});

onClickOutside(
  exploreMenu,
  () => {
    isExploreOpen.value = false;
  },
  { ignore: [exploreActivator] },
);

const handleExploreClick = () => {
  isExploreOpen.value = !isExploreOpen.value;
};
const handleExploreLinkClick = () => {
  isExploreOpen.value = false;
};

// ── Mobile "More" sheet ──────────────────────────────────────────────
// Items NOT already in the bottom bar (Dashboard / Deals / Projects / Learn).
// Tiers + Profile live in the avatar dropdown on desktop but are unreachable
// on mobile, so they go here too. Ask is the floating launcher, not an item.
type MoreItem = { key: TabKey; label: string; icon: string; to: string; isNew?: boolean };

const moreItems = computed<MoreItem[]>(() => {
  const all: MoreItem[] = [
    { key: 'developers', label: t('nav.items.developers'), icon: 'building-05', to: '/developers' },
    ...(canSee24Sold.value
      ? [{ key: 'resale' as TabKey, label: t('nav.items.secondaryMarket'), icon: 'building-05', to: '/resale' }]
      : []),
    { key: 'news', label: t('nav.items.news'), icon: 'document', to: '/news' },
    { key: 'tiers', label: t('nav.items.tiers'), icon: 'crown', to: '/rankings' },
    { key: 'profile', label: t('nav.items.profile'), icon: 'user', to: '/profile' },
  ];
  if (showServices.value) {
    all.unshift({ key: 'services', label: t('nav.items.services'), icon: 'services', to: '/services' });
    all.push({ key: 'myServices', label: t('servicesOrders.nav.myServices'), icon: 'document', to: '/my-services' });
  }
  if (canSeeFreeFeatures.value) {
    // Mirror the desktop tab bar: Marketing is surfaced for verified agents +
    // free-tier leads.
    all.unshift({ key: 'marketing', label: t('nav.items.marketing'), icon: 'edit-page', to: '/marketing' });
  }
  return all;
});

const isMoreOpen = ref(false);

// Close on route change
watch(
  () => route.path,
  () => {
    isMoreOpen.value = false;
  },
);

const { canSwitchLocale, enabledLocales, currentLocale, switchLocale } = useLocaleSwitch();

// Mobile "More" sheet has no room for the full unit/currency panel — it only
// gets the language entry (carried-over foundation minor for logged-in users).
const pickMobileLocale = (code: LocaleCode) => {
  isMoreOpen.value = false;
  switchLocale(code);
};
</script>

<template>
  <header
    class="sticky top-0 z-[100] flex h-[72px] items-center justify-between border-b border-black/[0.08] bg-white px-4 tablet:px-6 desktop:grid desktop:grid-cols-[1fr_auto_1fr] desktop:gap-4 desktop:px-8"
  >
    <div class="flex items-center gap-3 desktop:gap-4 desktop:justify-self-start">
      <UILogo
        :to="'/dashboard'"
        :size="28"
        variant="compact"
        aria-label="Insiders of Dubai"
      />
    </div>

    <nav
      class="hidden desktop:flex desktop:justify-self-center"
      :aria-label="t('nav.aria.primary')"
    >
      <ul class="flex h-[72px] items-center gap-6 xl:gap-7">
        <li
          v-for="tab in tabs"
          :key="tab.key"
        >
          <!-- Dropdown trigger (Explore) -->
          <div
            v-if="tab.submenu"
            :ref="
              (el) => {
                if (el) exploreActivator = el as HTMLDivElement;
              }
            "
            class="group relative flex h-[72px] cursor-pointer items-center gap-1.5 text-[14px] transition-colors duration-150"
            :class="
              activeKey === tab.key ? 'font-semibold text-black-100' : 'font-medium text-black-60 hover:text-black-100'
            "
            :aria-expanded="isExploreOpen"
            aria-haspopup="menu"
            role="button"
            tabindex="0"
            @click="handleExploreClick"
            @keydown.enter.prevent="handleExploreClick"
            @keydown.space.prevent="handleExploreClick"
          >
            {{ tab.label }}
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              class="text-black-60 transition-transform duration-150"
              :class="{ 'rotate-180': isExploreOpen }"
              aria-hidden="true"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
            <span
              v-if="activeKey === tab.key"
              class="absolute inset-x-0 -bottom-px h-[2px] bg-black-100"
              aria-hidden="true"
            />
          </div>

          <!-- Plain link tab -->
          <NuxtLink
            v-else
            :to="tab.to ?? '/'"
            class="group relative flex h-[72px] items-center gap-1.5 text-[14px] transition-colors duration-150"
            :class="
              activeKey === tab.key ? 'font-semibold text-black-100' : 'font-medium text-black-60 hover:text-black-100'
            "
            :aria-current="activeKey === tab.key ? 'page' : undefined"
          >
            {{ tab.label }}
            <span
              v-if="tab.isNew"
              class="inline-flex h-[16px] items-center rounded-full bg-gold-primary-120 px-1.5 font-mono text-[9px] font-bold uppercase tracking-[0.1em] text-black-100"
              :aria-label="t('nav.topNav.newFeatureAria')"
              >{{ t('nav.topNav.newBadge') }}</span
            >
            <span
              v-if="activeKey === tab.key"
              class="absolute inset-x-0 -bottom-px h-[2px] bg-black-100"
              aria-hidden="true"
            />
          </NuxtLink>
        </li>
      </ul>

      <ClientOnly>
        <div
          v-if="isExploreOpen"
          ref="exploreMenu"
          :style="exploreFloatingStyles"
          class="z-[110] w-[320px] overflow-hidden rounded-2xl border border-black/[0.08] bg-white p-2 shadow-floating"
          role="menu"
        >
          <NuxtLink
            v-for="item in exploreSubmenu"
            :key="item.key"
            :to="item.to"
            class="flex flex-col gap-0.5 rounded-xl px-3 py-2.5 transition-colors hover:bg-black-03"
            role="menuitem"
            @click="handleExploreLinkClick"
          >
            <span class="text-[14px] font-semibold text-black-100">{{ item.label }}</span>
            <span class="text-[12px] text-black-60">{{ item.description }}</span>
          </NuxtLink>
        </div>
      </ClientOnly>
    </nav>

    <div class="flex items-center gap-2 desktop:gap-3 desktop:justify-self-end">
      <NuxtLink
        v-if="showEarnings"
        :to="'/payments-history'"
        class="hidden items-center gap-3 whitespace-nowrap rounded-full border border-gold-primary-30 bg-gold-primary-10 px-3 py-1.5 transition-colors hover:border-gold-primary-100 hover:bg-gold-primary-20 xl:inline-flex"
        :aria-label="t('nav.topNav.earningsAria')"
      >
        <span
          class="font-mono text-[9.5px] font-semibold uppercase leading-none tracking-[0.14em] text-gold-primary-120"
        >
          {{ t('nav.topNav.earningsLabel') }}
        </span>
        <span class="font-serif text-[15px] font-semibold leading-none tracking-[-0.015em] text-black-100 tabular-nums">
          {{ earningsDisplay }}
        </span>
      </NuxtLink>

      <!-- Mobile "More" hamburger — hidden on desktop -->
      <button
        type="button"
        class="desktop:hidden flex h-9 w-9 items-center justify-center rounded-full text-black-60 transition-colors hover:bg-black-03 hover:text-black-100"
        :aria-expanded="isMoreOpen"
        :aria-label="t('nav.topNav.moreNavOptionsAria')"
        @click="isMoreOpen = !isMoreOpen"
      >
        <svg
          v-if="!isMoreOpen"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <line
            x1="3"
            y1="6"
            x2="21"
            y2="6"
          />
          <line
            x1="3"
            y1="12"
            x2="21"
            y2="12"
          />
          <line
            x1="3"
            y1="18"
            x2="21"
            y2="18"
          />
        </svg>
        <svg
          v-else
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <line
            x1="18"
            y1="6"
            x2="6"
            y2="18"
          />
          <line
            x1="6"
            y1="6"
            x2="18"
            y2="18"
          />
        </svg>
      </button>

      <BillingPlanChip />

      <PortalNotificationBell />

      <div
        v-if="authStore.user"
        ref="activator"
        class="flex cursor-pointer items-center gap-2"
        :aria-expanded="isMenuVisible"
        aria-haspopup="menu"
        role="button"
        tabindex="0"
        @click="isMenuVisible = !isMenuVisible"
        @keydown.enter.prevent="isMenuVisible = !isMenuVisible"
        @keydown.space.prevent="isMenuVisible = !isMenuVisible"
      >
        <span
          class="inline-flex h-[38px] w-[38px] items-center justify-center rounded-full font-display text-[13px] font-bold text-black-100"
          style="background: linear-gradient(135deg, #ffb648, #eda436)"
          aria-hidden="true"
        >
          {{ userInitials }}
        </span>
      </div>

      <ClientOnly>
        <div
          v-if="isMenuVisible && authStore.user"
          ref="menu"
          :style="floatingStyles"
          class="z-[110] max-h-[calc(100dvh-88px)] w-[280px] overflow-y-auto rounded-2xl border border-black/[0.08] bg-white shadow-floating"
          role="menu"
        >
          <div class="flex flex-col gap-1.5 border-b border-black/[0.08] px-4 py-3">
            <p class="font-serif text-[16px] font-semibold text-black-100">
              {{ userFullName }}
            </p>
            <p class="text-[12px] text-black-60">{{ authStore.user?.email }}</p>
            <AgentTierBadge
              v-if="agentStats?.tier"
              :tier="agentStats.tier"
              size="sm"
              class="mt-1 self-start"
            />
          </div>
          <PortalInsidersLicenceCard
            v-if="showInsidersLicence"
            variant="menu"
          />
          <PortalTopNavPreferences @locale-picked="isMenuVisible = false" />
          <nav class="flex flex-col py-1">
            <NuxtLink
              :to="'/profile'"
              class="flex h-10 items-center gap-2 px-4 text-[14px] text-black-80 transition-colors hover:bg-black-03"
              role="menuitem"
              @click="handleMenuLinkClick"
            >
              <UIIcon
                name="user"
                size="sm"
              />
              {{ t('nav.items.profile') }}
            </NuxtLink>
            <NuxtLink
              :to="'/profile'"
              class="flex h-10 items-center gap-2 px-4 text-[14px] text-black-80 transition-colors hover:bg-black-03"
              role="menuitem"
              @click="handleMenuLinkClick"
            >
              <UIIcon
                name="settings"
                size="sm"
              />
              {{ t('nav.topNav.accountSettings') }}
            </NuxtLink>
            <NuxtLink
              v-if="showServices"
              to="/my-services"
              class="flex h-10 items-center gap-2 px-4 text-[14px] text-black-80 transition-colors hover:bg-black-03"
              role="menuitem"
              @click="handleMenuLinkClick"
            >
              <UIIcon
                name="document"
                size="sm"
              />
              {{ t('servicesOrders.nav.myServices') }}
            </NuxtLink>
            <NuxtLink
              :to="'/rankings'"
              class="flex h-10 items-center gap-2 px-4 text-[14px] text-black-80 transition-colors hover:bg-black-03"
              role="menuitem"
              @click="handleMenuLinkClick"
            >
              <UIIcon
                name="crown"
                size="sm"
              />
              {{ t('nav.items.tiers') }}
            </NuxtLink>
            <NuxtLink
              v-if="authStore.getIsAccountManager"
              :to="'/am/dashboard'"
              class="flex h-10 items-center gap-2 px-4 text-[14px] text-black-80 transition-colors hover:bg-black-03"
              role="menuitem"
              @click="handleMenuLinkClick"
            >
              <UIIcon
                name="id"
                size="sm"
              />
              {{ t('nav.topNav.accountManager') }}
            </NuxtLink>
            <NuxtLink
              v-if="authStore.getIsAdmin"
              :to="'/admin/users'"
              class="flex h-10 items-center gap-2 px-4 text-[14px] text-black-80 transition-colors hover:bg-black-03"
              role="menuitem"
              @click="handleMenuLinkClick"
            >
              <UIIcon
                name="id"
                size="sm"
              />
              {{ t('nav.items.admin') }}
            </NuxtLink>
            <hr class="my-1 border-black/[0.08]" />
            <DeveloperPartnerShellProfileSwitcher @navigate="handleMenuLinkClick" />
            <hr class="my-1 border-black/[0.08]" />
            <button
              type="button"
              class="flex h-10 items-center gap-2 px-4 text-start text-[14px] text-black-80 transition-colors hover:bg-black-03"
              role="menuitem"
              @click="handleLogout"
            >
              <UIIcon
                name="log-out"
                size="sm"
              />
              {{ t('nav.items.logout') }}
            </button>
          </nav>
        </div>
      </ClientOnly>
    </div>
  </header>

  <!-- Mobile "More" sheet — teleported to body, desktop:hidden via JS guard -->
  <ClientOnly>
    <Teleport to="body">
      <Transition name="more-backdrop">
        <div
          v-if="isMoreOpen"
          class="desktop:hidden fixed inset-0 z-[90] bg-black/30"
          aria-hidden="true"
          @click="isMoreOpen = false"
        />
      </Transition>

      <Transition name="more-sheet">
        <nav
          v-if="isMoreOpen"
          class="desktop:hidden fixed inset-x-0 top-[72px] z-[95] overflow-hidden rounded-b-2xl border border-t-0 border-black/[0.08] bg-white shadow-floating"
          role="menu"
          :aria-label="t('nav.topNav.moreNavAria')"
        >
          <ul class="flex flex-col py-2">
            <li
              v-for="item in moreItems"
              :key="item.key"
              role="none"
            >
              <NuxtLink
                :to="item.to"
                class="flex h-12 items-center gap-3 px-5 text-[14px] font-medium text-black-80 transition-colors hover:bg-black-03"
                :class="activeKey === item.key ? 'font-semibold text-black-100 bg-black-03' : ''"
                :aria-current="activeKey === item.key ? 'page' : undefined"
                role="menuitem"
                @click="isMoreOpen = false"
              >
                <UIIcon
                  :name="item.icon"
                  size="sm"
                  class="text-black-60"
                />
                <span>{{ item.label }}</span>
                <span
                  v-if="item.isNew"
                  class="ms-auto inline-flex h-[16px] items-center rounded-full bg-gold-primary-120 px-1.5 font-mono text-[9px] font-bold uppercase tracking-[0.1em] text-black-100"
                  :aria-label="t('nav.topNav.newFeatureAria')"
                  >{{ t('nav.topNav.newBadge') }}</span
                >
              </NuxtLink>
            </li>
          </ul>

          <div class="border-t border-black/[0.08] py-2">
            <DeveloperPartnerShellProfileSwitcher
              variant="sheet"
              @navigate="isMoreOpen = false"
            />
          </div>

          <!-- Language (mobile) — no room for the desktop unit/currency panel here,
               so this sheet only gets the language entry. Hidden on staff paths /
               single-locale deployments via canSwitchLocale. -->
          <div
            v-if="canSwitchLocale"
            class="border-t border-black/[0.08] px-5 py-3"
          >
            <p class="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-black-60">
              {{ t('common.preferences.language') }}
            </p>
            <div class="mt-1.5 flex flex-wrap gap-1 rounded-lg bg-black-03 p-1">
              <button
                v-for="code in enabledLocales"
                :key="code"
                type="button"
                class="flex-1 whitespace-nowrap rounded-md px-2 py-1.5 text-[12px] font-semibold transition-colors"
                :class="
                  currentLocale === code
                    ? 'bg-white text-black-100 shadow-[0_1px_3px_rgba(13,15,21,0.06)]'
                    : 'text-black-60 hover:text-black-100'
                "
                :lang="getLocaleMeta(code).language"
                @click="pickMobileLocale(code)"
              >
                {{ getLocaleMeta(code).name }}
              </button>
            </div>
          </div>
        </nav>
      </Transition>
    </Teleport>
  </ClientOnly>
</template>

<style scoped lang="scss">
/* Mobile More sheet transitions */
.more-backdrop-enter-active,
.more-backdrop-leave-active {
  transition: opacity 0.2s ease;
}
.more-backdrop-enter-from,
.more-backdrop-leave-to {
  opacity: 0;
}

.more-sheet-enter-active,
.more-sheet-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}
.more-sheet-enter-from,
.more-sheet-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
